import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { ApiError, api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { WikiMaintenanceMenu } from './MaintenancePanel';

export function AdminWikiPages() {
  const queryClient = useQueryClient();
  const pagesQuery = useQuery({
    queryKey: ['admin', 'wiki', 'pages'],
    queryFn: api.adminWikiPages,
  });
  const categoriesQuery = useQuery({
    queryKey: ['admin', 'wiki', 'categories'],
    queryFn: api.adminWikiCategoriesList,
  });
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    title: string;
    takeaway: string;
    body: string;
    categorySlug: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 편집 모드 진입 시 페이지 본문을 로드
  const editingPageQuery = useQuery({
    queryKey: ['admin', 'wiki', 'page', editingSlug],
    queryFn: () => (editingSlug ? api.wikiPage(editingSlug) : Promise.reject('no-slug')),
    enabled: editingSlug !== null,
  });

  useEffect(() => {
    if (editingPageQuery.data && editingSlug) {
      setDraft({
        title: editingPageQuery.data.title,
        takeaway: editingPageQuery.data.takeaway,
        body: editingPageQuery.data.body,
        categorySlug: editingPageQuery.data.categorySlug ?? '',
      });
    }
  }, [editingPageQuery.data, editingSlug]);

  const updateMutation = useMutation({
    mutationFn: (args: {
      slug: string;
      title: string;
      takeaway: string;
      body: string;
      categorySlug: string | null;
    }) =>
      api.adminWikiPageUpdate(args.slug, {
        title: args.title,
        takeaway: args.takeaway,
        body: args.body,
        categorySlug: args.categorySlug,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'pages'] });
      queryClient.invalidateQueries({ queryKey: ['wiki'] });
      setEditingSlug(null);
      setDraft(null);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (args: { slug: string; reason: string }) =>
      api.adminWikiPageDelete(args.slug, args.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'wiki', 'pages'] });
      queryClient.invalidateQueries({ queryKey: ['wiki'] });
    },
  });

  const pages = pagesQuery.data?.pages ?? [];
  const categories = categoriesQuery.data?.categories ?? [];

  return (
    <PageShell
      header={
        <PageHeader
          title="위키 페이지 (전체)"
          count={pages.length}
          description="관리자는 LLM 이 작성한 페이지를 수동 편집/삭제할 수 있습니다."
          actions={<WikiMaintenanceMenu />}
        />
      }
    >
      <div className="space-y-6">
        {pagesQuery.isLoading ? <p className="text-sm text-foreground-muted">로딩 중…</p> : null}
        {error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        {pages.length === 0 ? (
          <div className="rounded-md border border-border bg-surface p-6 text-center text-foreground-muted">
            작성된 페이지가 없습니다.
          </div>
        ) : (
          <table className="w-full border-collapse text-sm">
            <thead className="border-b border-border">
              <tr>
                <th className="px-3 py-2 text-left font-medium">slug</th>
                <th className="px-3 py-2 text-left font-medium">제목</th>
                <th className="px-3 py-2 text-left font-medium">카테고리</th>
                <th className="px-3 py-2 text-left font-medium">업데이트</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {pages.map((p) => (
                <tr key={p.slug} className="border-b border-border-muted">
                  <td className="px-3 py-2 font-mono text-xs text-foreground-faint">
                    <Link
                      to="/wikis/p/$slug"
                      params={{ slug: p.slug }}
                      className="hover:text-accent"
                    >
                      {p.slug}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{p.title}</td>
                  <td className="px-3 py-2 text-foreground-muted">
                    {p.categorySlug ?? '(미분류)'}
                  </td>
                  <td className="px-3 py-2 text-xs text-foreground-faint">
                    {new Date(p.updatedAt).toLocaleString('ko-KR')}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingSlug(p.slug);
                        setError(null);
                      }}
                      className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:border-accent"
                    >
                      <Pencil className="h-3 w-3" />
                      편집
                    </button>{' '}
                    <button
                      type="button"
                      onClick={() => {
                        const reason = window.prompt(`"${p.title}" 삭제 사유:`, '관리자 수동 삭제');
                        if (reason && reason.length >= 5) {
                          deleteMutation.mutate({ slug: p.slug, reason });
                        }
                      }}
                      className="inline-flex items-center gap-1 rounded border border-destructive/40 px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {editingSlug && draft ? (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-3xl max-h-[90vh] overflow-auto rounded-md border border-border bg-background p-5 shadow-xl">
              <div className="mb-3 flex items-baseline justify-between">
                <h2 className="text-lg font-semibold">
                  페이지 편집 — <code className="font-mono text-sm">{editingSlug}</code>
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setEditingSlug(null);
                    setDraft(null);
                  }}
                  className="text-sm text-foreground-muted hover:text-foreground"
                >
                  닫기
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium" htmlFor="page-edit-title">
                    제목
                  </label>
                  <input
                    id="page-edit-title"
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.currentTarget.value })}
                    className="w-full rounded border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium" htmlFor="page-edit-category">
                    카테고리
                  </label>
                  <select
                    id="page-edit-category"
                    value={draft.categorySlug}
                    onChange={(e) => setDraft({ ...draft, categorySlug: e.currentTarget.value })}
                    className="w-full rounded border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
                  >
                    <option value="">(분류 없음)</option>
                    {categories.map((c) => (
                      <option key={c.slug} value={c.slug}>
                        {c.name} ({c.slug})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium" htmlFor="page-edit-takeaway">
                    takeaway (1~3 문장 핵심)
                  </label>
                  <textarea
                    id="page-edit-takeaway"
                    rows={3}
                    value={draft.takeaway}
                    onChange={(e) => setDraft({ ...draft, takeaway: e.currentTarget.value })}
                    className="w-full rounded border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium" htmlFor="page-edit-body">
                    본문 (마크다운)
                  </label>
                  <textarea
                    id="page-edit-body"
                    rows={20}
                    value={draft.body}
                    onChange={(e) => setDraft({ ...draft, body: e.currentTarget.value })}
                    className="w-full rounded border border-border bg-surface px-3 py-2 text-sm font-mono leading-6 focus:border-accent focus:outline-none"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      updateMutation.mutate({
                        slug: editingSlug,
                        title: draft.title,
                        takeaway: draft.takeaway,
                        body: draft.body,
                        categorySlug: draft.categorySlug.length > 0 ? draft.categorySlug : null,
                      })
                    }
                    disabled={updateMutation.isPending}
                    className="rounded bg-accent px-4 py-2 text-sm font-medium text-accent-foreground"
                  >
                    {updateMutation.isPending ? '저장 중…' : '저장'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingSlug(null);
                      setDraft(null);
                    }}
                    className="rounded border border-border px-4 py-2 text-sm"
                  >
                    취소
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </PageShell>
  );
}
